import { computed, inject, Injectable } from '@angular/core';
import { query, QueryError } from '@core/query';
import type { ShareErrorState, ShareView } from '../models/share-view';
import { toShareErrorState } from '../utilities/share-error-state';
import { toShareView } from '../utilities/to-share-view';
import { ShareApiService } from './share.api.service';

@Injectable({ providedIn: 'root' })
export class ShareResolverService {
    private readonly api = inject(ShareApiService);

    private readonly shareQuery = query((code: string) => this.loadShare(code));

    readonly share = this.shareQuery.value;
    readonly isLoading = this.shareQuery.isLoading;
    readonly isLoaded = this.shareQuery.isLoaded;
    readonly error = this.shareQuery.error;

    readonly errorState = computed<ShareErrorState | null>(() => {
        const error = this.error();

        return error ? toShareErrorState(error.code) : null;
    });

    resolve(code: string): Promise<ShareView | null> {
        return this.shareQuery.fetch(code);
    }

    reset(): void {
        this.shareQuery.reset();
    }


    private async loadShare(code: string): Promise<ShareView> {
        const result = await this.api.getShareByCode(code);

        if (result.isFailure()) {
            throw new QueryError(result.error.code, result.error.message);
        }

        const share = result.value;

        if (!share) {
            throw new QueryError('REPORT_NOT_FOUND', `No share exists for code "${code}".`);
        }

        return toShareView(share);
    }
}
